import {
  IconButton,
  Icon,
  Button,
  Tooltip,
  theme,
  Box,
} from '@everlywell/ui-kit';
import { NavigationArrow } from '@phosphor-icons/react';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import analytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import { logError } from 'utils/helpers';

export type ViewNearbyButtonProps = {
  onGetZipCode: (zipCode: string) => void;
};

/**
 * [View Nearby Button asks for permission to capture a user's location].
 */
const ViewNearbyButton = (props: ViewNearbyButtonProps) => {
  const { onGetZipCode } = props;
  const [userLocationPermissionDenied, setUserLocationPermissionDenied] =
    useState(false);

  const checkGeolocationPermission = async () => {
    const result = await navigator.permissions.query({ name: 'geolocation' });
    if (result.state === 'denied') {
      setUserLocationPermissionDenied(true);
    }
  };

  useEffect(() => {
    checkGeolocationPermission();
  }, []);

  const handleLocationError = (error: GeolocationPositionError) => {
    if (error.code === error.PERMISSION_DENIED) {
      setUserLocationPermissionDenied(true);
      logError('Error fetching user location', {
        component: 'QuestLocationFinder',
        method: 'getUserLocation',
        experience: 'Quest Product PDP',
      });
    }
  };

  const getUserLocation = () => {
    analytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        label: 'View nearby',
      },
    });
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(async (position) => {
        const { latitude, longitude } = position.coords;

        try {
          const convertedZipCode = await convertCoordinatesToZipCode(
            latitude,
            longitude,
          );
          onGetZipCode(convertedZipCode);
        } catch (error) {
          logError('Error capturing zip code', {
            component: 'QuestLocationFinder',
            method: 'navigator.geolocation.getCurrentPosition',
            experience: 'Quest Product PDP',
          });
        }
      }, handleLocationError);
    }
  };

  const disabledButtonMessage =
    'You declined permission to access your location. To allow access, update your settings and refresh the page.';
  return (
    <Tooltip
      hasArrow
      backgroundColor="tints.lightCream"
      boxShadow="tooltip"
      borderColor="tints.darkCream"
      borderStyle="solid"
      borderWidth="1px"
      arrowShadowColor={theme.colors.tints.darkCream}
      color="tints.darkGray"
      // width from design
      width="320px"
      placement="top-end"
      label={disabledButtonMessage}
      isDisabled={!userLocationPermissionDenied}
      shouldWrapChildren
    >
      <Box>
        <IconButton
          variant={'primary'}
          colorScheme="whiteCS"
          color={'tints.black'}
          onClick={getUserLocation}
          icon={<Icon as={NavigationArrow} />}
          aria-label="View nearby locations"
          display={{
            base: 'flex',
            tablet: 'none',
            desktop: 'none',
          }}
          size={['sm', 'md']}
          sx={{
            width: ['44px', '52px'], // TODO: add 'input' variant to Button to align with input fields
            height: ['44px', '52px'], // TODO: add 'input' variant to Button to align with input fields
          }}
          isDisabled={userLocationPermissionDenied}
        />
        <Button
          variant={'primary'}
          colorScheme="whiteCS"
          color={'tints.black'}
          onClick={getUserLocation}
          rightIcon={<Icon as={NavigationArrow} />}
          display={{
            base: 'none',
            tablet: 'flex',
            desktop: 'flex',
          }}
          size={'sm'}
          sx={{
            py: [2, 3], // TODO: add 'input' variant to Button to align with input fields
          }}
          isDisabled={userLocationPermissionDenied}
        >
          View nearby
        </Button>
      </Box>
    </Tooltip>
  );
};

const convertCoordinatesToZipCode = async (
  latitude: number,
  longitude: number,
) => {
  const response = await axios.get(
    `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${process.env.REACT_APP_GOOGLE_API_KEY}`,
  );

  const results = response.data.results;

  if (results.length > 0) {
    const addressComponents = results[0].address_components;
    const zipCodeComponent = addressComponents.find(
      (component: { types: string | string[] }) =>
        component.types.includes('postal_code'),
    );

    if (zipCodeComponent) {
      const zipCode = zipCodeComponent.long_name;

      return zipCode;
    }
  }
};

export default ViewNearbyButton;
